/* eslint-disable sonarjs/cognitive-complexity */
import { joinURL } from 'ufo'
import { api } from '~/api/apis'
import {
  httpErrorSchema,
  type HttpErrorType
} from '~/api/helper/api-error-handler'
import type { ApiResponse } from '~/api/helper/api-response-schema'
import { apiInstance } from '~/api/instance'
import { isType } from '~/utils/functions/is-type'

type Action = 'read' | 'create' | 'update' | 'delete'

export function useAppApi<T extends ApiResponse<unknown>>(
  requestApi: Promise<T>
) {
  async function call(options?: {
    action?: Action
    loading?: boolean
    ignoreStatus?: number[]
  }): Promise<
    | {
        success: boolean
        response: T
      }
    | {
        success: false
        response: ApiResponse<null>
      }
    | {
        success: true
        response: {
          status: 204
          message: string
          success: true
          total: null
          data: null
        }
      }
  > {
    const commonStore = useCommonStore()
    const { action, ignoreStatus, loading } = options ?? {}

    try {
      if (loading) {
        commonStore.setLoading({
          show: true
        })
      }

      const response = await requestApi

      if (action) {
        if (response.status === 200) {
          setSuccessSnack(options?.action)
        } else if (!ignoreStatus?.includes(response.status)) {
          setFailSnack({ action, status: response.status })
        }
      }

      return { success: response.success ?? true, response }
    } catch (error) {
      if (!isType(error, httpErrorSchema)) {
        return {
          success: false,
          response: {
            status: 500,
            success: false,
            total: 0,
            message: '系統錯誤',
            data: null
          }
        }
      }
      const errorData = error as HttpErrorType
      if (errorData.error.status === 401) {
        const result = await handleError(errorData)

        if (result?.success) {
          return {
            success: true,
            response: result as any
          }
        } else {
          return {
            success: false,
            response: result as any
          }
        }
      } else {
        return {
          success: false,
          response: {
            status: errorData.error.status,
            success: false,
            total: 0,
            message: errorData.error.message,
            data: error.error.responseData
          }
        }
      }
    } finally {
      if (loading) {
        commonStore.setLoading({
          show: false
        })
      }
    }
  }

  return {
    call
  }

  async function handleError(error: HttpErrorType) {
    const { formatUrl } = useUrl()
    const authStore = useAuthStore()
    const { baseURL } = useEnvStore()

    try {
      if (authStore.refreshToken && authStore.accountNo) {
        await api.auth.refreshAdminToken()
      } else if (authStore.refreshToken) {
        await api.auth.refreshAppToken()
      } else {
        await api.auth.getAppToken()
      }

      const { url, requestOptions } = error.requestInfo

      return await apiInstance<ApiResponse<any>>(
        formatUrl(url, true),
        requestOptions
      )
    } catch {
      removeAuthCookies()
      const redirectUrl = joinURL(baseURL, '/')

      if (window.location.pathname !== redirectUrl) {
        window.location.href = redirectUrl
      }

      return undefined
    }
  }

  function setSuccessSnack(action?: Action) {
    const commonStore = useCommonStore()

    switch (action) {
      case 'create':
        commonStore.setSnack({
          action: 'success',
          message: '新增成功'
        })
        break
      case 'update':
        commonStore.setSnack({
          action: 'success',
          message: '編輯成功'
        })
        break
      case 'delete':
        commonStore.setSnack({
          action: 'success',
          message: '刪除成功'
        })
    }
  }

  function setFailSnack(options: { action: Action; status: number }) {
    const commonStore = useCommonStore()
    const { action, status } = options

    if (status === 204) {
      return
    }

    switch (action) {
      case 'create':
        commonStore.setSnack({ action: 'fail', message: '新增失敗' })
        break
      case 'read':
        commonStore.setSnack({ action: 'fail', message: '無法取得資料' })
        break
      case 'update':
        commonStore.setSnack({ action: 'fail', message: '編輯失敗' })
        break
      case 'delete':
        commonStore.setSnack({ action: 'fail', message: '刪除失敗' })
        break
    }
  }
}
